import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/pagination';

gsap.registerPlugin(ScrollTrigger);

const WhyCommitWatch = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const cardsRef = useRef(null);

  const values = [
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
        </svg>
      ),
      title: 'Developer-Friendly',
      description: 'Built with love by developers who understand your workflow. No bloat, no distractions—just the commits you care about, on GitHub or GitLab.',
      gradient: 'from-accent-500 to-emerald-400',
    },
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
        </svg>
      ),
      title: 'Lightweight',
      description: 'Runs efficiently in the background with minimal memory footprint. Your browser stays fast and responsive.',
      gradient: 'from-blue-500 to-cyan-400',
    },
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
        </svg>
      ),
      title: 'Privacy-First',
      description: 'Zero tracking. Zero analytics. Zero data collection. Your GitHub and GitLab activity is none of our business.',
      gradient: 'from-purple-500 to-pink-400',
    },
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
        </svg>
      ),
      title: 'Open Source',
      description: 'Fully open source and transparent. Audit the code, contribute features, or fork it for your team.',
      gradient: 'from-orange-500 to-amber-400',
    },
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Always Updated',
      description: 'Automatic updates ensure you always have the latest features and security patches without lifting a finger.',
      gradient: 'from-rose-500 to-red-400',
    },
    {
      icon: (
        <svg className="w-10 h-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
        </svg>
      ),
      title: 'Free Forever',
      description: 'No premium tiers, no subscriptions, no catch. Commit Watch is and will always be completely free.',
      gradient: 'from-teal-500 to-green-400',
    },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Header animation
      gsap.fromTo(headerRef.current?.children || [],
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: headerRef.current,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Cards stagger animation with 3D effect
      const cards = cardsRef.current?.querySelectorAll('.value-card') || [];
      cards.forEach((card, index) => {
        gsap.fromTo(card,
          { 
            y: 80, 
            opacity: 0,
            rotateX: 15,
            scale: 0.9,
          },
          {
            y: 0,
            opacity: 1,
            rotateX: 0,
            scale: 1,
            duration: 0.8,
            delay: index * 0.1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: card,
              start: 'top 85%',
              toggleActions: 'play none none reverse',
            },
          }
        );
      });

      // CTA button animation
      gsap.fromTo('.why-cta',
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: '.why-cta',
            start: 'top 90%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      // Parallax background orbs
      gsap.to('.why-orb', {
        y: -100,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1.5,
        },
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  // Simple hover effect for cards - no expensive mouse tracking
  const handleCardEnter = (e) => {
    const card = e.currentTarget;
    gsap.to(card, {
      scale: 1.01,
      y: -6,
      duration: 0.3,
      ease: 'power2.out',
    });
  };

  const handleCardLeave = (e) => {
    const card = e.currentTarget;
    gsap.to(card, {
      scale: 1,
      y: 0,
      duration: 0.3,
      ease: 'power2.out',
    });
  };

  return (
    <section
      ref={sectionRef}
      className="relative py-16 md:py-20 overflow-hidden"
      id="why"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-dark-950" />
      <div className="absolute inset-0 bg-gradient-to-r from-accent-500/5 via-transparent to-blue-500/5" />
      
      {/* Decorative orbs */}
      <div className="why-orb absolute -left-40 top-1/4 w-[500px] h-[500px] bg-accent-500/5 rounded-full blur-[150px]" />
      <div className="why-orb absolute -right-40 bottom-1/4 w-[400px] h-[400px] bg-blue-500/5 rounded-full blur-[120px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section header */}
        <div ref={headerRef} className="text-center mb-12 sm:mb-16 md:mb-20">
          <span className="inline-block px-4 sm:px-5 py-2 mb-6 sm:mb-8 text-xs sm:text-sm font-semibold text-accent-400 bg-accent-500/10 rounded-full border border-accent-500/20 tracking-wide uppercase">
            Why Choose Us
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight px-2">
            Built for Developers,{' '}
            <span className="gradient-text">By Developers</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-dark-400 max-w-2xl mx-auto px-2">
            We obsess over the details so you can focus on what matters: shipping great code.
          </p>
        </div>

        {/* Cards - Desktop grid */}
        <div
          ref={cardsRef}
          className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8"
        >
          {values.map((value, index) => (
            <div
              key={index}
              className="value-card group relative p-6 sm:p-8 rounded-2xl sm:rounded-3xl glass border border-white/5 hover:border-accent-500/30 transition-colors duration-500 overflow-hidden"
              onMouseEnter={handleCardEnter}
              onMouseLeave={handleCardLeave}
            >
              {/* Gradient background on hover */}
              <div className={`absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-500 bg-gradient-to-br ${value.gradient}`} />
              
              {/* Icon with gradient */}
              <div className={`relative z-10 w-14 h-14 sm:w-18 sm:h-18 mb-4 sm:mb-6 rounded-xl sm:rounded-2xl bg-gradient-to-br ${value.gradient} p-[2px]`}>
                <div className="w-full h-full bg-dark-900 rounded-xl sm:rounded-2xl flex items-center justify-center text-white group-hover:bg-dark-800 transition-colors p-3 sm:p-4">
                  {value.icon}
                </div>
              </div>

              <h3 className="relative z-10 text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4 group-hover:text-accent-400 transition-colors">
                {value.title}
              </h3>
              <p className="relative z-10 text-dark-400 leading-relaxed text-base sm:text-lg">
                {value.description}
              </p>
            </div>
          ))}
        </div>

        {/* Mobile Swiper */}
        <div className="sm:hidden">
          <Swiper
            modules={[Autoplay, Pagination]}
            spaceBetween={12}
            slidesPerView={1.15}
            centeredSlides={true}
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
            }}
            pagination={{ clickable: true }}
            className="!pb-14"
          >
            {values.map((value, index) => (
              <SwiperSlide key={index}>
                <div className="p-5 sm:p-7 rounded-2xl glass border border-white/5">
                  <div className={`w-14 h-14 sm:w-16 sm:h-16 mb-4 sm:mb-5 rounded-xl bg-gradient-to-br ${value.gradient} p-[1px]`}>
                    <div className="w-full h-full bg-dark-900 rounded-xl flex items-center justify-center text-white p-3">
                      {value.icon}
                    </div>
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">
                    {value.title}
                  </h3>
                  <p className="text-dark-400 leading-relaxed text-sm sm:text-base">
                    {value.description}
                  </p>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
};

export default WhyCommitWatch;
